'use client';
import { Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import PaymentModal from './PaymentModal.tsx';
import { z } from 'zod';
import { ALPHABET_REGEX, PRICE_REGEX } from '../../constants/regex.ts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserContext } from '../../contexts/UserContext.tsx';
import { formatNumberWithComma } from '../../utils/number.ts';
import { Trans, useTranslation } from 'react-i18next';
import CheckPermission from '../common/CheckPermission.tsx';
import { Accounting } from '../../enum/accounting.tsx';
import Autocomplete from '@mui/material/Autocomplete';
import axios from 'axios';
import { useDebounce } from 'use-debounce';
import { DELAY_BEFORE_SEARCH } from '../../constants/numbers.ts';
import PaymentModalAdmin from './PaymentModalAdmin.tsx';

const AddBalance = () => {
	const { t } = useTranslation();
	const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
	const [amount, setAmount] = useState<number | null>(null);
	const [options, setOptions] = useState<any[]>([]);
	const [inputValue, setInputValue] = useState('');
	const [_, setLoading] = useState(false);
	const [search] = useDebounce(inputValue, DELAY_BEFORE_SEARCH);
	const [val, setVal] = useState<any | null>(null);
	const { userBalance } = useContext(UserContext);
	const { user } = useContext(UserContext);
	const formattedBalance = formatNumberWithComma(userBalance);
	const [selectedUserBalance, setSelectedUserBalance] = useState<number | null>(null);
	const formattedBalanceUser = formatNumberWithComma(selectedUserBalance ?? undefined);
	const descZod = z
		.string()
		.regex(ALPHABET_REGEX, { message: t('invalid_description_message') })
		.min(3, t('invalid_minـdescription_message'));

	const ticketSchema = z.object({
		price: z
			.string()
			.regex(PRICE_REGEX, { message: t('invalid_price_message') })
			.min(5, t('invalid_minـprice_message')),

		description: user?.is_admin ? descZod : descZod.optional(),
	});

	const {
		register,
		formState: { errors, isValid },
		setValue,
		getValues,
		trigger,
		watch,
		reset,
	} = useForm<z.infer<typeof ticketSchema>>({
		resolver: zodResolver(ticketSchema),
	});
	const handleOpenPaymentModal = () => {
		if (user?.is_admin) {
			if (isValid && val) {
				setIsPaymentModalOpen(true);
			}
		} else {
			if (isValid) {
				setIsPaymentModalOpen(true);
			}
		}
	};

	const handleClosePaymentModal = () => {
		setIsPaymentModalOpen(false);
	};

	const handleAddAmount = (val: number) => {
		const newAmount = val;
		setAmount(newAmount);
		setValue('price', newAmount.toString(), { shouldValidate: true });
		trigger('price');
	};
	useEffect(() => {
		user?.is_admin &&
			(async () => {
				setLoading(true);
				axios({
					url: 'admin/users',
					params: {
						search: JSON.stringify([
							{
								field: 'name',
								value: search,
							},
						]),
						limit: 50,
					},
				}).then(res => {
					if (res.data.success) {
						setOptions(res.data.data);
					}
				});
				setLoading(false);

				setOptions([...options]);
			})();
	}, [search]);

	const handleReset = () => {
		setAmount(null);
		setVal(null);
		reset();
	};

	const handleSearch = (_: any, newValue: any) => {
		setVal(newValue);
		if (newValue && !user?.is_admin) {
			axios({
				url: `/admin/wallets/user-balance/${newValue.id}`,
			}).then(res => {
				if (res.data.success) {
					setSelectedUserBalance(res.data.data);
				} else {
					setSelectedUserBalance(null);
				}
			});
			setLoading(false);
		} else {
			setSelectedUserBalance(null);
		}
	};
	const role = Accounting.WalletIndex;

	return userBalance === undefined ? (
		<Box className="flex min-h-96 w-full items-center justify-center">
			<CircularProgress />
		</Box>
	) : (
		<>
			<div className="flex h-full w-full flex-col items-center justify-center">
				<div className="w-full rounded-lg bg-white">
					<div className="mx-6 mt-10 flex flex-col items-center justify-center gap-4 md:flex-row md:justify-between">
						<Typography className="text-center font-medium md:text-left">{t('add-wallet-balance')}</Typography>
						<div className="relative mt-4 rounded bg-gradient-to-r from-primary-main to-secondary-main p-0.5 md:mt-0">
							<img className="md:w-18 md:h-18 absolute -right-8 -top-2 h-16 w-16" src="/images/finance/add-charge-balance.svg" alt="add charge balance picture" />
							<div className="h-11 w-full md:w-80">
								<div className="bg-background-paper">
									<CheckPermission permissions={role}>
										{user?.is_admin ? (
											selectedUserBalance !== null ? (
												<div className="mt-4 flex h-11 w-full items-center justify-between rounded bg-white pe-3 ps-10">
													<Typography>{t('current-balance')}</Typography>
													<Trans i18nKey="tooman_amount_label" values={{ amount: formattedBalanceUser }} components={[<Typography />]} />
												</div>
											) : (
												''
											)
										) : (
											<div className="flex h-11 w-full items-center justify-between rounded bg-white pe-3 ps-10">
												<Typography>{t('current-balance')}</Typography>
												<Trans i18nKey="tooman_amount_label" values={{ amount: formattedBalance }} components={[<Typography />]} />
											</div>
										)}
									</CheckPermission>
								</div>
							</div>
						</div>
					</div>
					{user?.is_admin && (
						<Autocomplete
							className={'mt-8 px-4'}
							getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
							filterOptions={x => x}
							options={options}
							autoComplete
							includeInputInList
							filterSelectedOptions
							value={val}
							onChange={handleSearch}
							noOptionsText={t('no_user')}
							onInputChange={(_, newInputValue) => {
								setInputValue(newInputValue);
							}}
							renderOption={(props, option) => {
								return (
									<MenuItem {...props} key={option.id} value={val}>
										{option.name}
									</MenuItem>
								);
							}}
							renderInput={params => <TextField {...params} label={`${t('search_user')} `} fullWidth></TextField>}
						/>
					)}
					<form className="mt-6 flex flex-col items-center justify-center gap-4 px-4">
						{user?.is_admin && <TextField className={'mb-6 mt-6'} variant={'outlined'} fullWidth label={t('description')} InputLabelProps={{ shrink: !!watch('description') }} {...register('description')} error={!!errors.description} helperText={errors.description?.message} />}
						<Stack direction="column" className="flex w-full items-center justify-between gap-4 sm:flex-row">
							<Button variant="outlined" className="w-full sm:w-60" onClick={() => handleAddAmount(1000000)}>
								1,000,000 تومان
							</Button>
							<Button variant="outlined" className="w-full sm:w-60" onClick={() => handleAddAmount(3000000)}>
								3,000,000 تومان
							</Button>
							<Button variant="outlined" className="w-full sm:w-60" onClick={() => handleAddAmount(5000000)}>
								5,000,000 تومان
							</Button>
						</Stack>
						<TextField
							className="w-full"
							placeholder={t('enter-price')}
							value={amount}
							type="number"
							{...register('price')}
							error={!!errors.price}
							helperText={errors.price?.message}
							onChange={e => {
								setAmount(parseInt(e.target.value));
								setValue('price', e.target.value, { shouldValidate: true });
								trigger('price');
							}}
							sx={{
								'& .MuiInputBase-input': {
									textAlign: 'center',
								},
							}}
						/>
						<div className="mb-8 mt-5 flex w-full flex-col items-center gap-3 sm:flex-row-reverse sm:justify-center">
							<Button variant="outlined" className="w-full border-action-disabledBackground text-action-disabled sm:w-28" onClick={handleReset}>
								{t('cancel')}
							</Button>
							<Button variant="contained" className="w-full sm:w-60" onClick={handleOpenPaymentModal} disabled={user?.is_admin ? !val || !isValid : !isValid}>
								{t('confirm')}
							</Button>
						</div>
					</form>
				</div>
			</div>
			{/* Payment Modal */}
			{user?.is_admin ? <PaymentModalAdmin handleBalance={handleSearch} enableType={false} open={isPaymentModalOpen} onClose={handleClosePaymentModal} handleResultServer={handleReset} amount={amount} description={getValues('description')} user_id={val?.id} /> : <PaymentModal rout={'/accounting-profile/increase-balance'} enableType={false} data={{ amount: amount }} amount={amount} open={isPaymentModalOpen} onClose={handleClosePaymentModal} handleResultServer={handleReset} />}{' '}
		</>
	);
};

export default AddBalance;
